// PostingViewDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box, Typography, Paper, IconButton } from '@mui/material';
import SummaryTab from '../requestingSubComponents/requestingSummaryTab'; 
import TransferBondLayout from '../tbondlayout';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import PostingPowers from './postingPowers';

const PostingViewDetails = () => {
  const [bondDetails, setBondDetails] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { canViewRequestingAgency } = location.state || { canViewRequestingAgency: false };

  const params = new URLSearchParams(location.search);
  const id = params.get('ID');

  useEffect(() => {
    // Scrolls the window to the top when the component is mounted
    window.scrollTo(0, 0);
  }, []);

  const fetchBondDetails = useCallback(async () => {
    if (!id) return;

    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;

    try {
      const response = await fetch(`${baseURL}requestingtransferbond/${id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching bond details: ${response.statusText}`);
      }

      const data = await response.json();
      setBondDetails({
        ...data,
        requestingAgencyName: data.requestingAgencyName || 'N/A',
      });
    } catch (error) {
      toast.error('Error fetching bond details.');
      console.error('Error fetching bond details:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchBondDetails();

    const intervalId = setInterval(() => {
      fetchBondDetails();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [fetchBondDetails]);

  const handleBack = () => {
    navigate('/post-transfer-bond-dashboard'); // Adjust the path as needed
  };

  const handleRefresh = () => {
    fetchBondDetails();
  };

  return (
    <TransferBondLayout>
      <Container maxWidth="lg" sx={{ paddingTop: 4 }}>
      <Box sx={{ padding: 0 }}>
          {/* Back Button and Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
            Posting Dashboard
          </Typography>
        </Box>

        {/* Instructional Section */}
        <Paper
          sx={{
            padding: 2,
            mb: 2,
            borderRadius: 1,
            backgroundColor: '#f9f9f9',
            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
            textAlign: 'left',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
          >
            In this section, you can:
          </Typography>
          <Box sx={{ lineHeight: 1.8 }}>
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: '1rem',
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: 18, mr: 1, color: 'primary.main' }}
              />{' '}
              Upload images and enter bond information.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: '1rem',
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: 18, mr: 1, color: 'primary.main' }}
              />{' '}
              Review the bond request details and status.
            </Typography>
          </Box>
        </Paper>

        {/* Posting Powers Component */}
        <Box sx={{ mt: 4 }}>
          <PostingPowers bondDetails={bondDetails} refreshHandler={handleRefresh} />
        </Box>

        {/* Summary Section */}
        <Box sx={{ mt: 4 }}>
          <SummaryTab bondDetails={bondDetails} canViewRequestingAgency={canViewRequestingAgency} />
        </Box>
      </Box>
      </Container>
    </TransferBondLayout>
  );
};

export default PostingViewDetails;
